import React, { useState, useEffect } from 'react';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import { useAuth } from '../../../hooks/useAuth';
import { useInstitutionsCollection, useFirestore } from '../../../hooks/useFirestore';
import { TransactionType } from '../../../types/transaction';
import { storage } from '../../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface FinancialEntryFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface Currency {
  code: string;
  symbol: string;
  name: string;
}

interface Institution {
  id: string; // ID Firestore du document
  institutionId: string; // Votre ID personnalisé (ex: INST77287191)
  name: string;
  currency?: Currency;
}

const FinancialEntryForm: React.FC<FinancialEntryFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { user } = useAuth();
  const { addDocument } = useFirestore('transactions');
  
  const {
    data: institutionsData,
    loading: institutionsLoading,
    getDocuments: getInstitutions
  } = useInstitutionsCollection();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [currency, setCurrency] = useState<Currency>({
    code: 'XOF',
    symbol: 'CFA',
    name: 'Franc CFA'
  });
  const [institutionName, setInstitutionName] = useState<string>('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const [formData, setFormData] = useState({
    type: 'income' as TransactionType,
    amount: '',
    description: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    const loadInstitutions = async () => {
      try {
        await getInstitutions();
      } catch (err) {
        console.error('Erreur lors du chargement des institutions:', err);
      }
    };

    loadInstitutions();
  }, [getInstitutions]);

  // CORRECTION: Rechercher par institutionId au lieu de id
  useEffect(() => {
    console.log('institutionsData:', institutionsData);
    console.log('user.institutionId:', user?.institutionId);
    
    if (user?.institutionId && institutionsData && institutionsData.length > 0) {
      const institutions: Institution[] = (institutionsData as any) || [];
      console.log('institutions array:', institutions);
      
      // CORRECTION ICI: Rechercher par institutionId, pas par id
      const userInstitution = institutions.find(inst => 
        inst.institutionId === user.institutionId
      );
      
      console.log('userInstitution trouvée (par institutionId):', userInstitution);
      
      if (userInstitution) {
        console.log('Currency de l\'institution:', userInstitution.currency);
        
        if (userInstitution.currency) {
          setCurrency({
            code: userInstitution.currency.code || 'XOF',
            symbol: userInstitution.currency.symbol || 'CFA',
            name: userInstitution.currency.name || 'Franc CFA'
          });
        } else {
          setCurrency({
            code: 'XOF',
            symbol: 'CFA',
            name: 'Franc CFA'
          });
        }
        
        setInstitutionName(userInstitution.name || '');
      } else {
        // CORRECTION: Aussi essayer de chercher par id au cas où
        const userInstitutionById = institutions.find(inst => 
          inst.id === user.institutionId
        );
        
        if (userInstitutionById) {
          console.log('userInstitution trouvée (par id Firestore):', userInstitutionById);
          
          if (userInstitutionById.currency) {
            setCurrency({
              code: userInstitutionById.currency.code || 'XOF',
              symbol: userInstitutionById.currency.symbol || 'CFA',
              name: userInstitutionById.currency.name || 'Franc CFA'
            });
          }
          setInstitutionName(userInstitutionById.name || '');
        } else {
          console.warn('Aucune institution trouvée avec institutionId:', user.institutionId);
          console.warn('IDs disponibles:', institutions.map(inst => ({
            firestoreId: inst.id,
            institutionId: inst.institutionId,
            name: inst.name
          })));
          
          setCurrency({
            code: 'XOF',
            symbol: 'CFA',
            name: 'Franc CFA'
          });
          setInstitutionName('');
        }
      }
    } else {
      if (!user?.institutionId) {
        console.warn('Pas d\'ID institution pour l\'utilisateur');
      }
      if (!institutionsData || institutionsData.length === 0) {
        console.warn('Aucune donnée d\'institutions chargée');
      }
    }
  }, [user?.institutionId, institutionsData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        setError('Format de fichier non supporté. Utilisez JPG, PNG ou PDF.');
        return;
      }

      if (file.size > maxSize) {
        setError('Le fichier est trop volumineux (max 5MB)');
        return;
      }

      setProofFile(file);
      setError('');

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl('');
      }
    }
  };

  const removeFile = () => {
    setProofFile(null);
    setPreviewUrl('');
  };

  const uploadProofFile = async (): Promise<string | null> => {
    if (!proofFile || !user?.institutionId) return null;

    try {
      setIsUploading(true);
      
      const timestamp = Date.now();
      const fileExtension = proofFile.name.split('.').pop();
      const fileName = `proof_${user.institutionId}_${timestamp}.${fileExtension}`;
      
      const storageRef = ref(storage, `proofs/${user.institutionId}/${fileName}`);
      
      await uploadBytes(storageRef, proofFile);
      
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      throw new Error('Échec de l\'upload du fichier');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.amount || !formData.description) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Montant invalide');
      return;
    }

    setIsLoading(true);

    try {
      let proofUrl = null;
      
      if (proofFile) {
        try {
          proofUrl = await uploadProofFile();
        } catch (uploadError) {
          console.error('Erreur lors de l\'upload:', uploadError);
          setError('Échec de l\'upload du fichier. Veuillez réessayer.');
          setIsLoading(false);
          return;
        }
      }

      const transactionData = {
        type: formData.type,
        amount: amount,
        description: formData.description,
        category: formData.category || '',
        date: formData.date,
        institutionId: user?.institutionId || '',
        institutionName: institutionName || '',
        createdBy: user?.uid || '',
        createdByName: user?.displayName || user?.email || '',
        status: 'pending',
        currency: currency,
        proofUrl: proofUrl,
        proofFileName: proofFile?.name || null,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      console.log('Transaction à enregistrer:', transactionData);

      await addDocument(transactionData);

      setFormData({
        type: 'income',
        amount: '',
        description: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
      });
      setProofFile(null);
      setPreviewUrl('');

      onSuccess();
    } catch (err) {
      console.error('Erreur lors de l\'ajout de la transaction:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Une erreur est survenue. Veuillez réessayer.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitting = isLoading || isUploading || institutionsLoading;

  const getButtonText = () => {
    if (isUploading) return "Upload du fichier...";
    if (isLoading) return "Enregistrement...";
    if (institutionsLoading) return "Chargement de la devise...";
    return "Enregistrer";
  };

  if (institutionsLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="text-gray-600">Chargement de la devise de l'institution...</p>
        <p className="text-sm text-gray-500">ID institution: {user?.institutionId || 'Non défini'}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Type de transaction */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type de transaction *
        </label>
        <select
          name="type"
          value={formData.type}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          required
          disabled={isSubmitting}
        >
          <option value="income">Entrée (Revenu)</option>
          <option value="expense">Sortie (Dépense)</option>
        </select>
      </div>

      {/* Montant avec devise */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Montant ({currency.symbol}) *
        </label>
        <div className="relative">
          <Input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            placeholder="0.00"
            step="0.01"
            min="0"
            className="pr-12"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span className="text-gray-500 font-medium">{currency.symbol}</span>
          </div>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Devise: {currency.name} ({currency.code})
        </p>
      </div>

      {/* Description */}
      <div>
        <Input
          label="Description *"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          disabled={isSubmitting}
          placeholder="Ex: Cotisation mensuelle des membres"
        />
      </div>

      {/* Catégorie */}
      <div>
        <Input
          label="Catégorie"
          name="category"
          value={formData.category}
          onChange={handleChange}
          disabled={isSubmitting}
          placeholder="Ex: Cotisations, Dons, etc."
        />
      </div>

      {/* Date (non modifiable) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date *
        </label>
        <Input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          disabled={true}
          className="bg-gray-50 cursor-not-allowed"
        />
        <p className="mt-1 text-xs text-gray-500">
          La date du jour est automatiquement sélectionnée
        </p>
      </div>

      {/* Élément de preuve */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Élément de preuve (optionnel)
        </label>
        
        {!proofFile ? (
          <div className="space-y-2">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg className="w-8 h-8 mb-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Cliquez pour uploader</span>
                </p>
                <p className="text-xs text-gray-500">
                  JPG, PNG ou PDF (max. 5MB)
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,image/jpg,application/pdf"
                disabled={isSubmitting}
              />
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            {previewUrl && (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Aperçu"
                  className="w-full h-48 object-cover rounded-lg border border-gray-200"
                />
              </div>
            )}
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                {proofFile.type.startsWith('image/') ? (
                  <svg className="w-6 h-6 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                  </svg>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                    {proofFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(proofFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={removeFile}
                disabled={isSubmitting}
                className="text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
        )}
        
        <p className="mt-2 text-xs text-gray-500">
          Uploader une facture, reçu ou tout autre document justificatif
        </p>
      </div>

      {/* Information sur l'institution */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-medium text-green-900 mb-2">Information institution</h4>
        <div className="space-y-1 text-sm">
          <div className="flex">
            <span className="font-medium text-green-700 w-40">Nom institution:</span>
            <span className="text-green-600">{institutionName || 'Non spécifiée'}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-green-700 w-40">Devise utilisée:</span>
            <span className="text-green-600 font-bold">{currency.name} ({currency.code})</span>
          </div>
          <div className="flex">
            <span className="font-medium text-green-700 w-40">Symbole monétaire:</span>
            <span className="text-green-600 font-bold text-lg">{currency.symbol}</span>
          </div>
          <div className="flex">
            <span className="font-medium text-green-700 w-40">ID Institution:</span>
            <span className="text-green-600 font-mono text-xs">{user?.institutionId || 'Non défini'}</span>
          </div>
        </div>
      </div>

      {/* Boutons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
        >
          {getButtonText()}
        </Button>
      </div>
    </form>
  );
};

export default FinancialEntryForm;